// TERMREADER1 (DEC-487): when taking a card, the floor sends the charge straight to the card reader this terminal
// remembers, and asks only when it has to. Pure decisions and words, no DOM, so each branch is testable on its own.

/** Why the floor is asking which card reader to use. */
export type ReaderAskReason = "none_remembered" | "reader_gone" | "reader_offline" | "switch";

export type ReaderBeforeCharge =
  /** A reader is remembered and not known to be offline: send the charge to it, no question. */
  | { kind: "send" }
  /** The terminal remembers no reader on this device: look for the server's memory, then ask once. */
  | { kind: "look_up_then_ask" }
  /** The remembered reader was just read as offline: ask instead of sending a charge that cannot work. */
  | { kind: "ask"; reason: "reader_offline" };

/** What the floor does when Confirm Payment is tapped on a card payment. `cachedHealth` is the reading the page header
 * already holds for the remembered reader (never a new call: Stripe says not to pre-check a reader in a sale). */
export function decideReaderBeforeCharge(params: {
  deviceReaderId: string | undefined;
  cachedHealth: "online" | "offline" | "unknown" | undefined;
}): ReaderBeforeCharge {
  if (params.deviceReaderId === undefined) return { kind: "look_up_then_ask" };
  if (params.cachedHealth === "offline") return { kind: "ask", reason: "reader_offline" };
  return { kind: "send" };
}

/** Server refusals that mean "this reader cannot take the charge, and nothing was charged". Each opens the question
 * instead of a dead end. Any other code keeps its ordinary message. */
const GONE_CODES: ReadonlySet<string> = new Set([
  "payment_terminal_not_found",
  "terminal_other_location",
  "terminal_provider_mismatch",
  "terminal_merchant_account_mismatch",
]);

export function readerAskReasonForRefusal(
  code: string | undefined,
): Extract<ReaderAskReason, "none_remembered" | "reader_gone" | "reader_offline"> | undefined {
  if (code === undefined) return undefined;
  if (code === "terminal_reader_offline") return "reader_offline";
  if (code === "terminal_not_assigned") return "none_remembered";
  return GONE_CODES.has(code) ? "reader_gone" : undefined;
}

/** What to do with the list of this location's readers when the device has none. */
export type ReaderChoice =
  | { kind: "proceed_without_reader" }
  | { kind: "use_only"; id: string; displayName: string }
  | { kind: "ask" };

/** A location with no readers lets the server answer (it has its own plain refusal); exactly one reader is the
 * reader; two or more is a person's decision. */
export function decideFromReaderList(
  readers: readonly { id: string; display_name: string }[],
): ReaderChoice {
  if (readers.length === 0) return { kind: "proceed_without_reader" };
  const only = readers[0];
  if (readers.length === 1 && only !== undefined) {
    return { kind: "use_only", id: only.id, displayName: only.display_name };
  }
  return { kind: "ask" };
}

export const READER_ASK_TITLE = "Choose a card reader";

export function readerAskLine(reason: ReaderAskReason, currentName?: string): string {
  switch (reason) {
    case "none_remembered":
      return "No card reader is chosen for this terminal yet. Nothing was charged. Pick one; it will be used for every card payment until you switch it.";
    case "reader_gone":
      return "The card reader this terminal used is no longer available. Nothing was charged. Pick another reader.";
    case "reader_offline":
      return `${currentName ?? "The card reader"} is offline. Nothing was charged. Pick another reader, or check this one and try again.`;
    case "switch":
      return "Pick the card reader for this terminal. It will be used for every card payment until you switch it.";
  }
}

export function chargingReaderLine(displayName: string | undefined): string {
  return displayName === undefined || displayName.trim() === ""
    ? "No card reader chosen yet"
    : `Card reader: ${displayName}`;
}

export const SWITCH_READER_LABEL = "Switch reader";
